import * as Tone from "tone";
import { useEffect, useState } from "react";
import { logger } from "../logger";
import { MutatableInternalState } from "../useMutatableInternalState";
import { Scale, ScaleType } from "../../constants/scales";
import { TranspositionPattern } from "../usePlayer";
import { SynthType } from "../useSynths";
import { Range } from "../../constants/ranges";
import convertMelodyRangeScaleToInternalState from "./convertMelodyRangeScaleToInternalState";
import { useMountEffect } from "@react-hookz/web";
import { getUserRangeFromStorage } from "../storeUserRange";
import { VocalRegister } from "../../constants/vocalRegister";

interface Params {
  mutateInternalState: (state: Partial<MutatableInternalState>) => void;
  onStop: () => void;
  defaultState: PlayerExternalState;
}

export interface PlayerExternalState {
  state: "stopped" | "playing";
  activeMelodyDegrees: Set<number>;
  activeChordDegrees: Set<number>;
  activeScale: Scale | undefined; // if no range it will be undefined
  holdScale: boolean;
  transpositionPattern: TranspositionPattern;
  volume: {
    [synth in SynthType]: number;
  };
  bpm: number;
  shorthands:
    | {
        melody: string;
      }
    | undefined;
  range?: Range;
  scaleType: ScaleType;
  vocalRegister: VocalRegister;
}

export default function useExternalState({
  mutateInternalState,
  onStop,
  defaultState,
}: Params) {
  // TODO: make this serializable — using too many maps and sets
  const [externalState, setExternalState] =
    useState<PlayerExternalState>(defaultState);

  useMountEffect(() => {
    const userStoredRange = getUserRangeFromStorage();

    if (userStoredRange) {
      logger("useExternalState: USER STORED RANGE ON MOUNT", userStoredRange);

      setExternalState((prev) => ({
        ...prev,
        range: userStoredRange,
      }));
    }
  });

  useEffect(() => {
    logger(
      "useExternalState: SYNC INTERNAL STATE WITH HOLDSCALE STATE",
      externalState.holdScale
    );

    mutateInternalState({
      holdScale: externalState.holdScale,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalState.holdScale]);

  useEffect(() => {
    logger(
      "useExternalState: SYNC INTERNAL STATE WITH TRANSPOSITIONPATTERN STATE",
      externalState.transpositionPattern
    );

    mutateInternalState({
      transpositionPattern: externalState.transpositionPattern,

      // default back to up every time the pattern changes
      transpositionDirection: "up",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalState.transpositionPattern]);

  useEffect(() => {
    logger(
      "useExternalState: SYNC INTERNAL STATE WITH SHORTHANDS.MELODY STATE/RANGE/SCALETYPE",
      externalState.shorthands?.melody
    );

    const newPartialState = convertMelodyRangeScaleToInternalState({
      shorthands: externalState.shorthands,
      range: externalState.range,
      scaleType: externalState.scaleType,
      vocalRegister: externalState.vocalRegister,
    });

    mutateInternalState(newPartialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    externalState.shorthands,
    externalState.scaleType,
    externalState.range,
    externalState.vocalRegister,
  ]);

  useEffect(() => {
    let stopTimeout: NodeJS.Timeout | null = null;

    const start = () => {
      clearTimeout(stopTimeout!);

      logger("useExternalState: ON START");

      setExternalState((prev) => ({
        ...prev,
        state: "playing",
      }));
    };

    const stop = (timeoutThenTryAgain = true) => {
      logger("useExternalState: ON STOP", Tone.Transport.now());

      onStop();

      setExternalState((prev) => ({
        ...prev,
        activeMelodyDegrees: new Set(),
        state: "stopped",
      }));

      clearTimeout(stopTimeout!);

      if (timeoutThenTryAgain) {
        // wait a little then make sure the note is released if it hasn't been played since
        // start will clear it if it has been played
        stopTimeout = setTimeout(() => {
          logger("useExternalState: STOP TIMEOUT TRY AGAIN");

          stop(false); // try again but don't make an endless loop
        }, 50);
      }
    };

    const stateChange = () => {
      logger("useExternalState: STATE CHANGE", Tone.context.state);

      if (Tone.context.state === "running") {
        return;
      }

      stop();
    };

    Tone.Transport.on("start", start);
    Tone.Transport.on("stop", stop);
    Tone.Transport.on("pause", stop);
    Tone.context.on("statechange", stateChange);

    return () => {
      Tone.Transport.off("start", start);
      Tone.Transport.off("stop", stop);
      Tone.Transport.off("pause", stop);
      Tone.context.off("statechange", stateChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [externalState, setExternalState] as const;
}
